import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/** Entity kinds whose coordinates the map editor can move. */
export type MapEntityKind = 'challenge' | 'stack' | 'artifact';

interface UpdateCoordsInput {
  kind: MapEntityKind;
  entityId: string;
  latitude: number;
  longitude: number;
}

const ENDPOINT_BY_KIND: Record<MapEntityKind, (eventId: string, entityId: string) => string> = {
  challenge: (eventId, entityId) => `/events/${eventId}/challenges/${entityId}`,
  stack: (eventId, entityId) => `/events/${eventId}/stacks/${entityId}`,
  artifact: (eventId, entityId) => `/events/${eventId}/artifacts/${entityId}`,
};

const QUERY_KEYS_BY_KIND: Record<MapEntityKind, string[]> = {
  challenge: ['EventChallenges', 'EventGroups'],
  stack: ['EventStacks', 'EventChallenges-summary'],
  artifact: ['EventArtifacts'],
};

/**
 * Persists a coordinate change from the map editor. One hook covers all three
 * pin kinds — challenges, stacks, artifacts — via their existing PATCH routes
 * (coordinates are content fields, editable in any non-archived status).
 */
export function useUpdateCoords(eventId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ kind, entityId, latitude, longitude }: UpdateCoordsInput) =>
      apiClient.patch(ENDPOINT_BY_KIND[kind](eventId, entityId), { latitude, longitude }),
    onSuccess: (_data, { kind }) => {
      for (const key of QUERY_KEYS_BY_KIND[kind]) {
        void queryClient.invalidateQueries({ queryKey: [key, eventId] });
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update location');
    },
  });
}
